import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from '@microsoft/signalr';

import api from '../services/apiClient';
import { userStatus } from '../enums/userStatus';

import { useMessage } from './message';
import { useUserStatus } from './userStatus';

interface AuthState {
  token?: string;
  user: string;
  administrador: boolean;
  hubConnection?: HubConnection | undefined;
}

interface LoginData {
  user: string;
  password: string;
}

interface AuthContextData {
  dataConnection: AuthState;
  signIn(data: LoginData): Promise<number>;
  reconnect(): Promise<void>;
  signOut(): void;
  registerUserStatus(
    accessToken: string,
    idConnection: string,
    status: number
  ): void;
  connectSignalR(): Promise<HubConnection>;
  loadLogoEmpresaSuporte(): void;
  urlLogoEmpresaSuporte: string;
  nameUser: string;
  setNameUser: (nameUser: string) => void;
  corUser: string;
  setCorUser: (corUser: string) => void;
}

interface URLSearchParamsEmp extends URLSearchParams {
  emp?: string;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

function useAuth(): AuthContextData {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

const AuthProvider: React.FC = ({ children }) => {
  const [hubConn, setHubConnection] = useState<HubConnection>();
  const {
    loadClientsWaiting,
    loadClientsAccepted,
    loadDataClienteRequestService,
    getChatGroupMsgPendente,
  } = useMessage();

  const { loadAllUsersStatus } = useUserStatus();
  const [urlLogoEmpresaSuporte, setUrlLogoEmpresaSuporte] = useState('');
  const [data, setData] = useState<AuthState>(() => {
    const token = localStorage.getItem('@LiveChat:token');
    const user = localStorage.getItem('@LiveChat:user');
    const administrador =
      localStorage.getItem('@LiveChat:administrador') === 'S';

    if (token && user && administrador && hubConn) {
      return { token, user, administrador, hubConn };
    }

    return {} as AuthState;
  });
  const [nameUser, setNameUser] = useState('');
  const [corUser, setCorUser] = useState('');

  const registerUserStatus = useCallback(
    (accessToken: string, idConnection: string, status: userStatus) => {
      const dataPatchUser = {
        idConnection,
        status,
      };

      api.patch('/login', dataPatchUser, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    },
    []
  );

  const loadLogoEmpresaSuporte = useCallback(async (): Promise<void> => {
    const tokenStorage = localStorage.getItem('@LiveChat:token');
    api
      .get('/parametro', {
        headers: { Authorization: `Bearer ${tokenStorage}` },
      })
      .then((response) => {
        const result = response.data;

        if (result.success) {
          setUrlLogoEmpresaSuporte(
            `${process.env.REACT_APP_API_URL}/LogoEmpresa/${result.data.seqEmpresa}/${result.data.logoEmpresaSuporte}`
          );
        }
      });
  }, []);

  const connectSignalR = useCallback(async (): Promise<HubConnection> => {
    const conn = new HubConnectionBuilder()
      .withUrl(`${process.env.REACT_APP_API_URL}/chat`)
      .configureLogging(LogLevel.Information)
      .build();

    await conn.start();

    conn.onclose(() => {
      setHubConnection({} as HubConnection);
      setData({
        token: '',
        user: '',
        administrador: false,
        hubConnection: hubConn,
      });
    });

    setHubConnection(conn);

    localStorage.setItem(
      '@LiveChat:connectionId',
      conn?.connectionId as string
    );

    const tokenStorage = localStorage.getItem('@LiveChat:token');
    const userStorage = localStorage.getItem('@LiveChat:user');
    const administradorStorage =
      localStorage.getItem('@LiveChat:administrador') === 'S';

    setData({
      token: tokenStorage === null ? data.token : String(tokenStorage),
      user: String(userStorage),
      administrador: administradorStorage,
      hubConnection: conn,
    });

    return conn;
  }, []);

  const reconnect = useCallback(async () => {
    const conn = await connectSignalR();

    const accessToken = String(localStorage.getItem('@LiveChat:token'));
    const user = String(localStorage.getItem('@LiveChat:user'));
    const administrador =
      String(localStorage.getItem('@LiveChat:administrador')) === 'S';

    setData({
      token: accessToken,
      user,
      administrador,
      hubConnection: conn,
    });

    const getUserStatus = Number(localStorage.getItem('@LiveChat:userStatus'));
    registerUserStatus(accessToken, String(conn.connectionId), getUserStatus);
    loadClientsWaiting();
    loadClientsAccepted();
    loadDataClienteRequestService();
    loadAllUsersStatus();
    getChatGroupMsgPendente();
  }, []);

  const signIn = useCallback(async ({ user, password }) => {
    const conn = await connectSignalR();

    const params: URLSearchParamsEmp = new Proxy(
      new URLSearchParams(window.location.search),
      {
        get: (searchParams, prop) => searchParams.get(String(prop)),
      }
    );

    const link = params?.emp;

    const dataPost = {
      email: '',
      password,
      accessKey: 'LiveChat',
      grantType: 'password',
      refreshToken: '',
      userName: user,
      connectionId: conn.connectionId,
      linkEmpresa: link,
    };

    const responseLogin = await api.post('/login', dataPost);
    const {
      accessToken,
      name,
      administrador,
      refreshToken,
      seqUsuario,
      seqEmpresa,
      master,
      cor,
    } = responseLogin.data;

    if (responseLogin.status === 400) return responseLogin.status;

    localStorage.setItem('@LiveChat:token', accessToken);
    localStorage.setItem('@LiveChat:refreshToken', refreshToken);
    localStorage.setItem('@LiveChat:user', user);
    localStorage.setItem('@LiveChat:nameUser', name);
    localStorage.setItem('@LiveChat:connectionId', conn.connectionId as string);
    localStorage.setItem('@LiveChat:administrador', administrador ? 'S' : 'N');
    localStorage.setItem('@LiveChat:master', master ? 'S' : 'N');
    localStorage.setItem('@LiveChat:seqUsuario', seqUsuario);
    localStorage.setItem('@LiveChat:seqEmpresa', seqEmpresa);
    localStorage.setItem('@LiveChat:linkEmpresa', String(link));
    localStorage.setItem('@LiveChat:corUser', cor);

    setData({
      token: accessToken,
      user,
      administrador,
      hubConnection: conn,
    });

    const statusUserLogin = localStorage.getItem('@LiveChat:userStatus');

    registerUserStatus(
      accessToken,
      String(conn.connectionId),
      Number(statusUserLogin)
    );

    return responseLogin.status;
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem('@LiveChat:token');
    localStorage.removeItem('@LiveChat:user');
    localStorage.removeItem('@LiveChat:nameUser');
    localStorage.removeItem('@LiveChat:connectionId');
    localStorage.removeItem('@LiveChat:administrador');
    localStorage.removeItem('@LiveChat:refreshToken');
    localStorage.removeItem('@LiveChat:seqUsuario');
    localStorage.removeItem('@LiveChat:seqEmpresa');
    localStorage.removeItem('@LiveChat:master');
    localStorage.removeItem('@LiveChat:chatUser');
    setData({} as AuthState);
    hubConn?.stop();
  }, []);

  const valueContext = useMemo(
    () => ({
      dataConnection: data,
      signIn,
      reconnect,
      signOut,
      registerUserStatus,
      connectSignalR,
      loadLogoEmpresaSuporte,
      urlLogoEmpresaSuporte,
      nameUser,
      setNameUser,
      corUser,
      setCorUser,
    }),
    [
      data,
      signIn,
      reconnect,
      signOut,
      registerUserStatus,
      connectSignalR,
      loadLogoEmpresaSuporte,
      urlLogoEmpresaSuporte,
      nameUser,
      setNameUser,
      corUser,
      setCorUser,
    ]
  );

  return (
    <AuthContext.Provider value={valueContext}>{children}</AuthContext.Provider>
  );
};

export { AuthProvider, useAuth };
